import i18next from 'i18next';
import { inject, injectable, named } from 'inversify';

import { DEFAULT_FEATURES, ENTITLEMENT } from '../constants';
import type { IntegrationType } from '../../types/config';
import CheckoutService from '../services/integrations/CheckoutService';
import AccountService, { type AccountServiceFeatures } from '../services/integrations/AccountService';
import SubscriptionService from '../services/integrations/SubscriptionService';
import JWPEntitlementService from '../services/entitlement/JWPEntitlementService';
import type { Offer } from '../../types/checkout';
import type { Plan } from '../../types/plans';
import type {
  AuthData,
  Capture,
  Customer,
  CustomerConsent,
  EmailConfirmPasswordInput,
  FirstLastNameInput,
  GetCaptureStatusResponse,
  LoginChallenge,
  SubscribeToNotificationsPayload,
} from '../../types/account';
import { assertFeature, assertModuleMethod, getNamedModule } from '../modules/container';
import { INTEGRATION_TYPE } from '../modules/types';
import type { ServiceResponse } from '../../types/service';
import { useAccountStore } from '../stores/AccountStore';
import { useConfigStore } from '../stores/ConfigStore';
import { useFavoritesStore } from '../stores/FavoritesStore';
import { useWatchHistoryStore } from '../stores/WatchHistoryStore';
import { FormValidationError } from '../errors/FormValidationError';
import { logError } from '../logger';
import { queryClient } from '../queryClient';
import EntitlementService from '../services/EntitlementService';
import FavoriteService from '../services/FavoriteService';
import WatchHistoryService from '../services/WatchHistoryService';

import WatchHistoryController from './WatchHistoryController';
import FavoritesController from './FavoritesController';
import AccessController from './AccessController';

@injectable()
export default class AccountController {
  private readonly checkoutService?: CheckoutService;
  private readonly accountService?: AccountService;
  private readonly subscriptionService?: SubscriptionService;
  private readonly entitlementService: JWPEntitlementService;
  private readonly accessController: AccessController;
  private readonly favoritesController: FavoritesController;
  private readonly favoriteService: FavoriteService;
  private readonly watchHistoryController: WatchHistoryController;
  private readonly watchHistoryService: WatchHistoryService;
  private readonly features: AccountServiceFeatures;

  private async refreshEntitlements() {
    await queryClient.invalidateQueries({ queryKey: ['entitlements'] });
  }

  constructor(
    @inject(INTEGRATION_TYPE) integrationType: IntegrationType,
    @inject(EntitlementService) @named(ENTITLEMENT.JWP) entitlementService: JWPEntitlementService,
    accessController: AccessController,
    favoritesController: FavoritesController,
    favoriteService: FavoriteService,
    watchHistoryController: WatchHistoryController,
    watchHistoryService: WatchHistoryService,
  ) {
    this.checkoutService = getNamedModule(CheckoutService, integrationType, false);
    this.accountService = getNamedModule(AccountService, integrationType, false);
    this.subscriptionService = getNamedModule(SubscriptionService, integrationType, false);
    this.entitlementService = entitlementService;
    this.favoriteService = favoriteService;
    this.watchHistoryService = watchHistoryService;

    // @TODO: Controllers shouldn't be depending on other controllers, but we've agreed to keep this as is for now
    this.accessController = accessController;
    this.favoritesController = favoritesController;
    this.watchHistoryController = watchHistoryController;

    this.features = integrationType && this.accountService ? this.accountService.features : DEFAULT_FEATURES;
  }

  validateSession = async ({ forceRefresh = false, reloadShelves = true }: { forceRefresh?: boolean; reloadShelves?: boolean } = {}) => {
    try {
      const authData = await this.getAuthData(forceRefresh);

      if (authData) {
        const config = useConfigStore.getState().config;
        const response = await this.accountService?.getUser({ config });

        if (response) {
          await this.onAuthenticated(response.user, response.customerConsents, { reloadShelves });
        }
      } else {
        // sign out the user when no auto data was returned
        await this.logout();
      }
    } catch (error: unknown) {
      logError('AccountController', 'Failed to get user', { error });

      // clear the session when the token was invalid
      // don't clear the session when the error is unknown (network hiccup or something similar)
      if (error instanceof Error && error.message.includes('Invalid JWT token')) {
        await this.logout();
      }
    }
  };

  initialize = async (url: string) => {
    useAccountStore.setState({ loading: true });

    if (this.accountService) {
      const config = useConfigStore.getState().config;
      await this.accountService.initialize(config, url, this.logout);

      // set the accessModel before restoring the user session
      useConfigStore.setState({ accessModel: this.accountService.accessModel });
      await this.validateSession({ reloadShelves: false });
    }

    await this.getEntitledPlans();

    useAccountStore.setState({ loading: false });
  };

  getSandbox() {
    return !!this.accountService?.sandbox;
  }

  updateUser = async (values: FirstLastNameInput | EmailConfirmPasswordInput): Promise<ServiceResponse<Customer>> => {
    assertModuleMethod(this.accountService?.updateCustomer, 'AccountService#updateCustomer is not available');

    useAccountStore.setState({ loading: true });

    try {
      const { user } = useAccountStore.getState();
      const { canUpdateEmail, canSupportEmptyFullName } = this.getFeatures();

      if (Object.prototype.hasOwnProperty.call(values, 'email') && !canUpdateEmail) {
        throw new Error('Email update not supported');
      }

      if (!user) {
        throw new Error('User not logged in');
      }

      const errors = this.validateInputLength(values as FirstLastNameInput);
      if (errors.length) {
        return {
          errors,
          responseData: {} as Customer,
        };
      }

      let payload = values;
      // this is needed as a fallback when the name is empty (cannot be empty on JWP integration)
      if (!canSupportEmptyFullName) {
        payload = { ...values, email: user.email };
      }

      const updatedUser = await this.accountService.updateCustomer({ ...payload, id: user.id.toString() });

      if (!updatedUser) {
        throw new Error('Unknown error');
      }

      useAccountStore.setState({ user: updatedUser });

      return { errors: [], responseData: updatedUser };
    } finally {
      useAccountStore.setState({ loading: false });
    }
  };

  login = async (email: string, password: string, referrer: string) => {
    assertModuleMethod(this.accountService?.login, 'AccountService#login is not available');
    useAccountStore.setState({ loading: true });

    try {
      const response = await this.accountService.login({ email, password, referrer });

      // the sign in was successful, but the user isn't signed in (a challenge need to completed first)
      if (response.isSignedIn) {
        await this.accessController?.generateAccessTokens();
        await this.onAuthenticated(response.user, response.customerConsents, { reloadShelves: true });
      }

      // a challenge is requested
      if (response.challenge) {
        return useAccountStore.getState().addChallenge(response.challenge, { email });
      }
      return;
    } catch (error: unknown) {
      if (error instanceof Error && error.message.toLowerCase().includes('invalid param email')) {
        throw new FormValidationError({ email: [i18next.t('account:login.wrong_email')] });
      }
    } finally {
      useAccountStore.setState({ loading: false });
    }

    // consider any unknown response as a wrong combinations error (we could specify this even more)
    throw new FormValidationError({ form: [i18next.t('account:login.wrong_combination')] });
  };

  confirmLogin = async ({ challenge, answer }: { challenge: LoginChallenge; answer: string }) => {
    assertModuleMethod(this.accountService?.confirmLogin, 'AccountService#confirmLogin is not available');

    return await this.accountService.confirmLogin({ challenge, answer });
  };

  loginExternal = async (provider: string, referrer: string) => {
    assertModuleMethod(this.accountService?.loginExternal, 'AccountService#loginExternal is not available');
    useAccountStore.setState({ loading: true });

    try {
      await this.accountService.loginExternal({ provider, referrer });
    } catch (error: unknown) {
      logError('AccountController', 'Failed to login with oauth', { error });
    } finally {
      useAccountStore.setState({ loading: false });
    }
  };

  logout = async () => {
    await this.accountService?.logout();
    await this.accessController?.removeAccessTokens();
    await this.clearLoginState();

    // let the application know to refresh all entitlements
    await this.refreshEntitlements?.();
  };

  register = async (email: string, password: string, referrer: string, consentsValues: CustomerConsent[], captchaValue?: string) => {
    assertModuleMethod(this.accountService?.register, 'AccountService#register is not available');

    try {
      const { favorites } = useFavoritesStore.getState();
      const { watchHistory } = useWatchHistoryStore.getState();

      const response = await this.accountService.register({
        email,
        password,
        consents: consentsValues,
        referrer,
        captchaValue,
        favorites: this.favoriteService.serializeFavorites(favorites),
        watchHistory: this.watchHistoryService.serializeWatchHistory(watchHistory),
      });

      // the create account was successful, but the user isn't signed in (a challenge need to completed first)
      if (response.isSignedIn) {
        const { user, customerConsents } = response;
        await this.onAuthenticated(user, customerConsents, { reloadShelves: false });
      }

      // a challenge is requested
      if (response.challenge) {
        return useAccountStore.getState().addChallenge(response.challenge, { email });
      }

      return;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();

        if (errorMessage.includes('customer already exists') || errorMessage.includes('account already exists')) {
          throw new FormValidationError({ form: [i18next.t('account:registration.user_exists')] });
        } else if (errorMessage.includes('please enter a valid e-mail address')) {
          throw new FormValidationError({ email: [i18next.t('account:registration.field_is_not_valid_email')] });
        } else if (errorMessage.includes('invalid param password')) {
          throw new FormValidationError({ password: [i18next.t('account:registration.invalid_password')] });
        }
      }
    }

    // in case the endpoint fails
    throw new FormValidationError({ form: [i18next.t('account:registration.failed_to_create')] });
  };

  updateConsents = async (customerConsents: CustomerConsent[]): Promise<ServiceResponse<CustomerConsent[]>> => {
    const { getAccountInfo } = useAccountStore.getState();
    const { customer } = getAccountInfo();

    useAccountStore.setState({ loading: true });

    try {
      const updatedConsents = await this.accountService?.updateCustomerConsents({
        customer,
        consents: customerConsents,
      });

      if (updatedConsents) {
        useAccountStore.setState({ customerConsents: updatedConsents });
        return {
          responseData: updatedConsents,
          errors: [],
        };
      }
      return {
        responseData: [],
        errors: [],
      };
    } finally {
      useAccountStore.setState({ loading: false });
    }
  };

  // TODO: Decide if it's worth keeping this or just leave combined with getUser
  // noinspection JSUnusedGlobalSymbols
  getCustomerConsents = async () => {
    assertModuleMethod(this.accountService?.getCustomerConsents, 'AccountService#getCustomerConsents is not available');

    const { getAccountInfo } = useAccountStore.getState();
    const { customer } = getAccountInfo();

    const consents = await this.accountService.getCustomerConsents({ customer });

    if (consents) {
      useAccountStore.setState({ customerConsents: consents });
    }

    return consents;
  };

  getPublisherConsents = async () => {
    assertModuleMethod(this.accountService?.getPublisherConsents, 'AccountService#getPublisherConsents is not available');

    const { config } = useConfigStore.getState();

    useAccountStore.setState({ publisherConsentsLoading: true });
    const consents = await this.accountService.getPublisherConsents(config);

    useAccountStore.setState({ publisherConsents: consents, publisherConsentsLoading: false });
  };

  getCaptureStatus = async (): Promise<GetCaptureStatusResponse> => {
    assertModuleMethod(this.accountService?.getCaptureStatus, 'AccountService#getCaptureStatus is not available');

    const { getAccountInfo } = useAccountStore.getState();
    const { customer } = getAccountInfo();

    return this.accountService.getCaptureStatus({ customer });
  };

  updateCaptureAnswers = async (capture: Capture): Promise<Capture> => {
    assertModuleMethod(this.accountService?.updateCaptureAnswers, 'AccountService#updateCaptureAnswers is not available');

    const { getAccountInfo } = useAccountStore.getState();
    const { customer, customerConsents } = getAccountInfo();

    const updatedCustomer = await this.accountService.updateCaptureAnswers({ customer, ...capture });

    useAccountStore.setState({
      user: updatedCustomer,
      customerConsents,
    });

    return updatedCustomer;
  };

  resetPassword = async (email: string, resetUrl: string) => {
    assertModuleMethod(this.accountService?.resetPassword, 'AccountService#resetPassword is not available');

    await this.accountService.resetPassword({
      customerEmail: email,
      resetUrl,
    });
  };

  changePasswordWithOldPassword = async (oldPassword: string, newPassword: string, newPasswordConfirmation: string) => {
    assertModuleMethod(this.accountService?.changePasswordWithOldPassword, 'AccountService#changePasswordWithOldPassword is not available');

    await this.accountService.changePasswordWithOldPassword({
      oldPassword,
      newPassword,
      newPasswordConfirmation,
    });
  };

  changePasswordWithToken = async (customerEmail: string, newPassword: string, resetPasswordToken: string, newPasswordConfirmation: string) => {
    assertModuleMethod(this.accountService?.changePasswordWithResetToken, 'AccountService#changePasswordWithResetToken is not available');

    try {
      await this.accountService.changePasswordWithResetToken({
        customerEmail,
        newPassword,
        resetPasswordToken,
        newPasswordConfirmation,
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes('Invalid code provided')) {
        throw new FormValidationError({ code: ["We don't recognize this verification code. Please try again."] });
      }
      throw error;
    }
  };

  updateSubscription = async (status: 'active' | 'cancelled'): Promise<unknown> => {
    assertModuleMethod(this.subscriptionService?.updateSubscription, 'SubscriptionService#updateSubscription is not available');

    const { getAccountInfo } = useAccountStore.getState();
    const { customerId } = getAccountInfo();
    const { subscription } = useAccountStore.getState();

    if (!subscription) throw new Error('user has no active subscription');

    const response = await this.subscriptionService.updateSubscription({
      customerId,
      offerId: subscription.offerId,
      status,
      unsubscribeUrl: subscription.unsubscribeUrl,
    });

    if (response.errors.length > 0) throw new Error(response.errors[0]);

    await this.reloadSubscriptions({ retry: 10 });

    return response?.responseData;
  };

  updateCardDetails = async ({
    cardName,
    cardNumber,
    cvc,
    expMonth,
    expYear,
    currency,
  }: {
    cardName: string;
    cardNumber: string;
    cvc: number;
    expMonth: number;
    expYear: number;
    currency: string;
  }) => {
    assertModuleMethod(this.subscriptionService?.updateCardDetails, 'SubscriptionService#updateCardDetails is not available');

    const { getAccountInfo } = useAccountStore.getState();
    const { customerId } = getAccountInfo();

    const response = await this.subscriptionService.updateCardDetails({
      cardName,
      cardNumber,
      cvc,
      expMonth,
      expYear,
      currency,
    });
    const activePayment = (await this.subscriptionService.getActivePayment({ customerId })) || null;

    useAccountStore.setState({
      loading: false,
      activePayment,
    });
    return response;
  };

  checkEntitlements = async (offerId?: string): Promise<boolean> => {
    assertModuleMethod(this.checkoutService?.getEntitlements, 'CheckoutService#getEntitlements is not available');

    if (!offerId) {
      return false;
    }

    const { responseData } = await this.checkoutService.getEntitlements({ offerId });
    return !!responseData?.accessGranted;
  };

  // This currently supports only one plan, as the current usage for the media metadata requires only one plan_id provided.
  // TODO: Support for multiple plans should be added. Revisit this logic once the dependency on plan_id is changed.
  getEntitledPlans = async (): Promise<Plan | null> => {
    const { config, settings } = useConfigStore.getState();
    const siteId = config.siteId;
    const isAccessBridgeEnabled = !!settings?.apiAccessBridgeUrl;

    // This should be only used when access bridge is defined, regardless of the integration type.
    if (!isAccessBridgeEnabled) {
      return null;
    }

    const response = await this.entitlementService.getEntitledPlans({ siteId });
    if (response?.plans?.length) {
      // Find the SVOD plan or fallback to the first available plan
      const entitledPlan = response.plans.find((plan) => plan.metadata.access_model === 'svod') || response.plans[0];
      useAccountStore.setState({ entitledPlan });
      return entitledPlan;
    }

    return null;
  };

  reloadSubscriptions = async (
    { delay, retry, offerId }: { delay?: number; retry?: number; offerId?: string } = {
      delay: 0,
      retry: 0,
    },
  ): Promise<unknown> => {
    assertModuleMethod(this.subscriptionService?.getActiveSubscription, 'SubscriptionService#getActiveSubscription is not available');
    assertModuleMethod(this.subscriptionService?.getAllTransactions, 'SubscriptionService#getAllTransactions is not available');
    assertModuleMethod(this.subscriptionService?.getActivePayment, 'SubscriptionService#getActivePayment is not available');

    useAccountStore.setState({ loading: true });

    const { getAccountInfo } = useAccountStore.getState();
    const { customerId } = getAccountInfo();
    const { accessModel } = useConfigStore.getState();

    // The subscription data takes a few seconds to load after it's purchased,
    // so here's a delay mechanism to give it time to process
    if (delay && delay > 0) {
      return new Promise((resolve: (value?: unknown) => void) => {
        setTimeout(() => {
          this.reloadSubscriptions({ retry, offerId }).finally(resolve);
        }, delay);
      });
    }

    // For non-SVOD platforms, there could be TVOD items, so we only reload entitlements
    if (accessModel !== 'SVOD') {
      await this.refreshEntitlements?.();
      const passes = await this.subscriptionService.getActivePasses?.({ customerId, svodOfferIds: this.accountService?.svodOfferIds });

      return useAccountStore.setState({ loading: false, passes: passes || [] });
    }

    const [activeSubscription, transactions, activePayment, passes] = await Promise.all([
      this.subscriptionService.getActiveSubscription({ customerId }),
      this.subscriptionService.getAllTransactions({ customerId }),
      this.subscriptionService.getActivePayment({ customerId }),
      this.subscriptionService.getActivePasses?.({ customerId, svodOfferIds: this.accountService?.svodOfferIds }),
    ]);

    let pendingOffer: Offer | null = null;

    // we use `startsWith` to support localised offers
    // @todo: move all offer matching utils to Cleeng services
    const foundOffer = !!offerId && (activeSubscription?.offerId === offerId || passes?.some((pass) => offerId.startsWith(pass.offerId)));

    if (!activeSubscription && !foundOffer && !passes?.some((pass) => pass.accessScope === 'all-content') && !!retry && retry > 0) {
      const retryDelay = 1500; // Any initial delay has already occurred, so we can set this to a fixed value

      return await this.reloadSubscriptions({ delay: retryDelay, retry: retry - 1, offerId });
    }

    // resolve and fetch the pending offer after upgrade/downgrade
    try {
      if (activeSubscription?.pendingSwitchId) {
        assertModuleMethod(this.checkoutService?.getOffer, 'getOffer is not available in checkout service');
        assertModuleMethod(this.checkoutService?.getSubscriptionSwitch, 'getSubscriptionSwitch is not available in checkout service');

        const switchOffer = await this.checkoutService.getSubscriptionSwitch({ switchId: activeSubscription.pendingSwitchId });
        const customerCountry = getAccountInfo().customer.country;
        const offerResponse = await this.checkoutService.getOffer({
          offerId: switchOffer.responseData.toOfferId,
          customerCountry,
        });

        pendingOffer = offerResponse.responseData;
      }
    } catch (error: unknown) {
      logError('AccountController', 'Failed to fetch the pending offer', { error });
    }

    useAccountStore.setState({
      subscription: activeSubscription,
      passes: passes || [],
      loading: false,
      pendingOffer,
      transactions,
      activePayment,
    });

    // let the app know to refresh the entitlements
    await this.refreshEntitlements?.();
  };

  exportAccountData = async () => {
    const { canExportAccountData } = this.getFeatures();

    assertModuleMethod(this.accountService?.exportAccountData, 'exportAccountData is not available in account service');
    assertFeature(canExportAccountData, 'Export account');

    return this.accountService.exportAccountData(undefined);
  };

  getExternalLoginProviders = (redirectUrl: string) => {
    return this.accountService?.getExternalLoginProviders?.({ redirectUrl }) || [];
  };

  getCustomerPortalUrl = (redirectUrl: string) => {
    return this.accountService?.getCustomerPortalUrl?.({ redirectUrl });
  };

  deleteAccountData = async (password: string) => {
    const { canDeleteAccount } = this.getFeatures();

    assertModuleMethod(this.accountService?.deleteAccount, 'deleteAccount is not available in account service');
    assertFeature(canDeleteAccount, 'Delete account');

    try {
      await this.accountService.deleteAccount({ password });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message.toLowerCase() : '';

      if (message.includes('invalid credentials')) {
        throw new FormValidationError({ form: [i18next.t('user:account.delete_account.invalid_credentials')] });
      }

      throw new FormValidationError({ form: [i18next.t('user:account.delete_account.error')] });
    }
  };

  getReceipt = async (transactionId: string) => {
    assertModuleMethod(this.subscriptionService?.fetchReceipt, 'fetchReceipt is not available in subscription service');

    const { responseData } = await this.subscriptionService.fetchReceipt({ transactionId });

    return responseData;
  };

  getAuthData = async (forceRefresh = false) => {
    return this.accountService?.getAuthData(forceRefresh) || null;
  };

  subscribeToNotifications = async ({ uuid, onMessage }: SubscribeToNotificationsPayload) => {
    assertModuleMethod(this.accountService?.subscribeToNotifications, 'AccountService#subscribeToNotifications is not available');

    return this.accountService.subscribeToNotifications({ uuid, onMessage });
  };

  getFeatures() {
    return this.features;
  }

  private async onAuthenticated(user: Customer, customerConsents: CustomerConsent[] | null, options: { reloadShelves: boolean }) {
    useAccountStore.setState({
      user,
      customerConsents,
    });

    await Promise.allSettled([
      this.reloadSubscriptions(),
      this.getPublisherConsents(),
      options.reloadShelves ? this.favoritesController.restoreFavorites() : Promise.resolve(),
      options.reloadShelves ? this.watchHistoryController.restoreWatchHistory() : Promise.resolve(),
    ]);
  }

  private validateInputLength = (values: { firstName: string; lastName: string }) => {
    const errors: string[] = [];
    if (Number(values?.firstName?.length) > 50) {
      errors.push(i18next.t('account:validation.first_name'));
    }
    if (Number(values?.lastName?.length) > 50) {
      errors.push(i18next.t('account:validation.last_name'));
    }

    return errors;
  };

  private clearLoginState = async () => {
    useAccountStore.setState({
      user: null,
      subscription: null,
      passes: [],
      transactions: null,
      activePayment: null,
      customerConsents: null,
      publisherConsents: null,
    });

    useConfigStore.setState({ accessModel: this.accountService?.accessModel });

    await this.favoritesController.restoreFavorites();
    await this.watchHistoryController.restoreWatchHistory();
  };

  /**
   * VD Private code below
   */

  hasDeviceFlow = () => !!this.accountService?.generateCode;

  setAuthData = (authData: AuthData) => {
    assertModuleMethod(this.accountService?.setAuthData, 'setAuthData is not available in account service');

    return this.accountService.setAuthData({ authData });
  };

  generateCode = async (deviceId: string, deviceName: string) => {
    assertModuleMethod(this.accountService?.generateCode, 'generateCode is not available in account service');

    const response = await this.accountService.generateCode({ deviceId, deviceName });

    return { ...response, deviceId, deviceName };
  };

  authorizeCode = async (code: string) => {
    assertModuleMethod(this.accountService?.authorizeCode, 'authorizeCode is not available in account service');

    try {
      await this.accountService.authorizeCode({ code });
    } catch (error: unknown) {
      throw new FormValidationError({ code: [i18next.t('account:login_device.error_title')] });
    }

    return true;
  };

  checkCode = async (params: { code: string; deviceId: string; id?: string }) => {
    assertModuleMethod(this.accountService?.checkCode, 'checkCode is not available in account service');

    const { refreshToken, accessToken: jwt } = await this.accountService.checkCode(params);

    await this.setAuthData({ jwt, refreshToken });
    await this.validateSession({ reloadShelves: true });

    return true;
  };
}
